import React, { useState } from 'react';
import { CheckCircleIcon, AlertCircleIcon, RefreshCwIcon, MessageCircleIcon, LoaderIcon } from 'lucide-react';
import { WhatsAppConnectionModal } from './WhatsAppConnectionModal';
import { useWhatsAppStatus, useConnectWhatsApp, useDisconnectWhatsApp } from '../hooks/useWhatsAppStatus';
import { useWhatsAppStore } from '../stores/whatsappStore';

interface WhatsAppStatusWidgetProps {
  compact?: boolean;
}

export function WhatsAppStatusWidget({
  compact = false
}: WhatsAppStatusWidgetProps) {
  // Fetch real connection status from backend
  const { data: status, isLoading } = useWhatsAppStatus();
  const connectMutation = useConnectWhatsApp();
  const disconnectMutation = useDisconnectWhatsApp();
  const isQRModalOpen = useWhatsAppStore((state) => state.isQRModalOpen);
  const setQRModalOpen = useWhatsAppStore((state) => state.setQRModalOpen);
  const [showDisconnectModal, setShowDisconnectModal] = useState(false);

  const isConnected = status?.connected ?? false;
  const isConnecting = status?.status === 'connecting' || status?.status === 'reconnecting';
  const phoneNumber = status?.phoneNumber;

  const handleConnect = () => {
    // Modal will handle connection initialization
    setQRModalOpen(true);
  };

  const handleConnectionSuccess = () => {
    setQRModalOpen(false);
    // Status will be updated automatically via TanStack Query
  };

  const handleDisconnect = async () => {
    try {
      await disconnectMutation.mutateAsync();
      setShowDisconnectModal(false);
    } catch (error) {
      console.error('Failed to disconnect WhatsApp:', error);
    }
  };

  if (compact) {
    return (
      <>
        <button
          onClick={handleConnect}
          disabled={isQRModalOpen || isLoading}
          className="w-10 h-10 bg-[#5B8E87] bg-opacity-10 rounded-full flex items-center justify-center hover:bg-opacity-20 transition-colors relative flex-shrink-0 disabled:opacity-60 disabled:cursor-not-allowed"
          title={isQRModalOpen ? 'Connecting...' : isConnected ? 'WhatsApp Connected' : 'Connect WhatsApp'}
        >
          {isQRModalOpen ? (
            <LoaderIcon className="w-5 h-5 text-[#5B8E87] animate-spin" />
          ) : (
            <MessageCircleIcon className="w-5 h-5 text-[#5B8E87]" />
          )}
          <div
            className={`absolute bottom-0 right-0 w-3 h-3 rounded-full border-2 border-white ${
              isConnected ? 'bg-[#5B8E87]' : 'bg-red-500'
            }`}
          />
        </button>
        {isQRModalOpen && (
          <WhatsAppConnectionModal
            onClose={() => setQRModalOpen(false)}
            onSuccess={handleConnectionSuccess}
          />
        )}
      </>
    );
  }

  return (
    <>
      <div
        className={`bg-white rounded-2xl shadow-sm p-4 border ${
          isConnected ? 'border-[#5B8E87]' : 'border-yellow-300'
        }`}
      >
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-3">
            {isLoading || isConnecting ? (
              <div className="w-10 h-10 rounded-full bg-gray-100 flex items-center justify-center">
                <RefreshCwIcon className="w-5 h-5 text-gray-500 animate-spin" />
              </div>
            ) : isConnected ? (
              <div className="w-10 h-10 rounded-full bg-[#5B8E87] bg-opacity-10 flex items-center justify-center">
                <CheckCircleIcon className="w-6 h-6 text-[#5B8E87]" />
              </div>
            ) : (
              <div className="w-10 h-10 rounded-full bg-yellow-50 flex items-center justify-center">
                <AlertCircleIcon className="w-6 h-6 text-[#C9A870]" />
              </div>
            )}
            <div>
              <p
                className={`font-semibold text-sm ${
                  isConnected ? 'text-[#5B8E87]' : 'text-[#C9A870]'
                }`}
              >
                {isLoading
                  ? 'Checking status...'
                  : isConnecting
                  ? 'Connecting...'
                  : isConnected
                  ? 'WhatsApp Connected'
                  : 'Not connected'}
              </p>
              <p className="text-xs text-gray-600">
                {isConnected
                  ? phoneNumber
                    ? `Active: ${phoneNumber}`
                    : 'Your WhatsApp is active'
                  : 'Connect to send messages'}
              </p>
            </div>
          </div>
          {!isLoading && !isConnecting && (
            <div className="flex gap-2">
              {isConnected ? (
                <button
                  onClick={() => setShowDisconnectModal(true)}
                  disabled={disconnectMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-colors bg-red-50 text-red-600 hover:bg-red-100 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                </button>
              ) : (
                <button
                  onClick={handleConnect}
                  disabled={connectMutation.isPending}
                  className="px-4 py-2 rounded-lg font-medium text-sm transition-colors disabled:opacity-50 disabled:cursor-not-allowed bg-[#5B8E87] text-white hover:bg-[#4F7A74]"
                >
                  {connectMutation.isPending ? 'Connecting...' : 'Connect Now'}
                </button>
              )}
            </div>
          )}
        </div>
      </div>
      {isQRModalOpen && (
        <WhatsAppConnectionModal
          onClose={() => setQRModalOpen(false)}
          onSuccess={handleConnectionSuccess}
        />
      )}
      {showDisconnectModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 z-50 flex items-center justify-center p-4">
          <div className="bg-white rounded-3xl w-full max-w-sm overflow-hidden animate-scale-in">
            <div className="p-6">
              <h3 className="text-lg font-semibold text-[#212121] mb-2">
                Disconnect WhatsApp?
              </h3>
              <p className="text-gray-600 mb-6">
                This will log out your WhatsApp connection. You'll need to scan the QR code again to reconnect.
              </p>
              <div className="flex gap-3">
                <button
                  onClick={() => setShowDisconnectModal(false)}
                  disabled={disconnectMutation.isPending}
                  className="flex-1 bg-gray-100 text-gray-600 font-semibold py-3 rounded-xl hover:bg-gray-200 transition-colors disabled:opacity-50"
                >
                  Cancel
                </button>
                <button
                  onClick={handleDisconnect}
                  disabled={disconnectMutation.isPending}
                  className="flex-1 bg-red-500 text-white font-semibold py-3 rounded-xl hover:bg-red-600 transition-colors disabled:opacity-50"
                >
                  {disconnectMutation.isPending ? 'Disconnecting...' : 'Disconnect'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </>
  );
}